import { ReactNode } from 'react';
import styled from 'styled-components';
import { RhythmInput, RhythmPattern, ta, warmUpLockTaps } from '../../../../../common/src/rhythm_echo.ts';
import { ensureUnreachable } from '../../../../../common/src/util.ts';
import { PrimaryButton } from '../learn_buttons.tsx';
import { VisibleCue } from '../learn_cue.ts';
import { ProgressPipsRow } from '../learn_screen_parts.tsx';
import { RhythmBlocks, RhythmLight } from './rhythm_blocks.tsx';
import { calibrationBpm, CalibrationPhase } from './rhythm_calibration_run.ts';
import { resultColorCss } from './rhythm_colors.ts';
import { RhythmCueRow } from './rhythm_cue_row.tsx';
import { RhythmNotation } from './rhythm_notation.tsx';
import {
  BlocksRow,
  CueSlack,
  FootRow,
  HintRow,
  NotationRow,
  rhythmPipTestId,
  ScreenBody,
  ScreenSubtitle,
  ScreenTitle,
  VoiceMeterRow,
} from './rhythm_screen_parts.tsx';
import { rhythmSpaceBarHint, RhythmTapButton } from './rhythm_tap_button.tsx';

export const rhythmCalibrationScreenId = 'rhythm-calibration';
export const rhythmCalibrationNextButtonId = 'rhythm-calibration-next';
export const rhythmCalibrationRetryButtonId = 'rhythm-calibration-retry';
export const rhythmWarmUpRowTestId = 'rhythm-warm-up-row';

// The bar the warm-up runs on, four quarter notes: one block per click
export const warmUpPattern: RhythmPattern = [ta, ta, ta, ta];

// The warm-up bar that opens a first session (issue #3523). There is no calibration screen any
// more: this is the pattern screen the singer will see for every pattern, built from the same
// rows in the same order (rhythm_screen_parts.tsx) - the "Warm-up · 70 bpm" row with its pips, the
// title and line, the syllable blocks, the notation row, the Ready · Set · Go! cue taking the
// slack, the tap button, the hint and the foot row Stop keeps on a pattern, so nothing moves when
// Next hands over to Pattern 1 - so the first thing a first-time singer sees is the exercise
// itself. The first bar is heard, not tapped; from there the clicks keep coming until the taps
// lock in, and the pips fill green with the agreeing taps. Locked and retry replace the tap button
// with Next and Try again, the way every result screen ends.
//
// Presentational: the page owns the run (rhythm_calibration_run.ts) and passes in what its
// selectors say, so a story can render any instant.
//
// Sing Rhythm's warm-up (issue #3723) is the same screen with `voiceMeter`: the singer sings "ta"
// with each click instead of tapping, so the meter stands where the tap button was, as it does on
// that exercise's pattern screen.
export const RhythmCalibrationScreen = ({
  phase,
  lights,
  cue,
  streak,
  tapOpen,
  onTap,
  onNext,
  onRetry,
  voiceMeter,
}: {
  voiceMeter?: ReactNode; // the "Hearing you" meter, on a warm-up the singer sings
  phase: CalibrationPhase;
  lights: RhythmLight[]; // one per block, see calibrationLights
  cue?: VisibleCue; // the cue word pulsing now, see calibrationCue
  streak: number; // agreeing taps so far, 0..warmUpLockTaps: the pips
  tapOpen: boolean; // a tap would count right now, from half a beat before Go!; also the button look
  onTap: (perfMs: number) => void;
  onNext: () => void;
  onRetry: () => void;
}) => {
  const end = warmUpEndButton(phase, onNext, onRetry);
  const input: RhythmInput = voiceMeter === undefined ? 'tap' : 'voice';
  return (
    <ScreenBody id={rhythmCalibrationScreenId} data-phase={phase} data-input={input}>
      <ProgressPipsRow
        label={`Warm-up · ${calibrationBpm} bpm`}
        color="green"
        pips={Array.from({ length: warmUpLockTaps }, (_pip, pip) => (pip < streak ? 'done' : 'todo'))}
        rowTestId={rhythmWarmUpRowTestId}
        pipTestId={rhythmPipTestId}
      />
      <ScreenTitle>
        {warmUpTitle(phase)}
        {phase === 'locked' && <Check> ✓</Check>}
      </ScreenTitle>
      <ScreenSubtitle>{warmUpSubtitle(phase, input)}</ScreenSubtitle>
      <BlocksRow>
        <RhythmBlocks pattern={warmUpPattern} lights={lights} />
      </BlocksRow>
      <NotationRow>
        <RhythmNotation pattern={warmUpPattern} />
      </NotationRow>
      <CueSlack>
        <RhythmCueRow cue={cue} large={input === 'voice'} />
      </CueSlack>
      {end ? (
        <PrimaryButton id={end.id} data-testid={end.id} onClick={end.onClick}>
          {end.label}
        </PrimaryButton>
      ) : input === 'voice' ? (
        <VoiceMeterRow>{voiceMeter}</VoiceMeterRow>
      ) : (
        <RhythmTapButton open={tapOpen} caption={phase === 'tap' ? 'with each click' : 'on Go!'} onTap={onTap} />
      )}
      <HintRow>{end || input === 'voice' ? '' : rhythmSpaceBarHint}</HintRow>
      <FootRow />
    </ScreenBody>
  );
};

// The button the run ends on in place of the tap button, or undefined while it is still running:
// Next hands over to the patterns, Try again starts another run
function warmUpEndButton(
  phase: CalibrationPhase,
  onNext: () => void,
  onRetry: () => void,
): { id: string; label: string; onClick: () => void } | undefined {
  if (phase === 'locked') {
    return { id: rhythmCalibrationNextButtonId, label: 'Next', onClick: onNext };
  }
  if (phase === 'retry') {
    return { id: rhythmCalibrationRetryButtonId, label: 'Try again', onClick: onRetry };
  }
  return undefined;
}

export function warmUpTitle(phase: CalibrationPhase): string {
  switch (phase) {
    case 'listen':
    case 'tap':
      return 'Warm-up';
    case 'locked':
      return 'Warm-up done';
    case 'retry':
      return 'Once more';
    default:
      return ensureUnreachable(phase);
  }
}

export function warmUpSubtitle(phase: CalibrationPhase, input: RhythmInput = 'tap'): string {
  const voice = input === 'voice';
  switch (phase) {
    case 'listen':
      return voice ? 'Listen, then sing “ta” with the clicks.' : 'Listen, then tap with the clicks.';
    case 'tap':
      return voice ? 'Sing “ta” with each click.' : 'Tap with each click.';
    case 'locked':
      return voice ? 'Voice synced. Now the patterns.' : 'Taps synced. Now the patterns.';
    case 'retry':
      return voice ? 'One “ta” per click.' : 'One tap per click.';
    default:
      return ensureUnreachable(phase);
  }
}

const Check = styled.span`
  color: ${resultColorCss.green};
`;
